/**
 * Diagnostic-only client identifiers sent on every API request (see docs/AUTOSAVE_DETAILS.md
 * §18 and the admin database logs page). Not used for auth or authorization.
 *
 * - Device id: persists forever on this device (localStorage), shared across all tabs.
 * - Tab id: lives only for this tab's lifetime (sessionStorage) -- a fresh tab (including a
 *   duplicated one, since sessionStorage is not cloned across a real "open new tab" action in
 *   most browsers) gets its own id.
 *
 * Both fall back to an in-memory value if storage is unavailable (private browsing, storage
 * quota, etc.) so a request always carries some id rather than throwing.
 */

const DEVICE_ID_STORAGE_KEY = 'calc_device_id';
const TAB_ID_STORAGE_KEY = 'calc_tab_id';

let inMemoryDeviceId: string | null = null;
let inMemoryTabId: string | null = null;

function newId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `id-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function readOrCreate(storage: Storage | null, key: string, memoFallback: string | null, setMemoFallback: (id: string) => void): string {
    if (!storage) {
        if (!memoFallback) {
            const id = newId();
            setMemoFallback(id);
            return id;
        }
        return memoFallback;
    }

    try {
        const existing = storage.getItem(key);
        if (existing) {
            return existing;
        }
        const id = newId();
        storage.setItem(key, id);
        return id;
    } catch {
        // Storage unavailable/blocked (e.g. private browsing quota) -- fall back in-memory.
        if (!memoFallback) {
            const id = newId();
            setMemoFallback(id);
            return id;
        }
        return memoFallback;
    }
}

function getStorage(kind: 'local' | 'session'): Storage | null {
    try {
        return kind === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
        return null;
    }
}

/** Persistent id for this device -- forever, shared across all tabs/windows. */
export function getDeviceId(): string {
    return readOrCreate(getStorage('local'), DEVICE_ID_STORAGE_KEY, inMemoryDeviceId, (id) => { inMemoryDeviceId = id; });
}

/** Id for this tab only -- lasts for the tab's lifetime, not shared with other tabs. */
export function getTabId(): string {
    return readOrCreate(getStorage('session'), TAB_ID_STORAGE_KEY, inMemoryTabId, (id) => { inMemoryTabId = id; });
}
